import { Routes } from '@angular/router';
import { AppPaths } from '../core/router/app-paths';
import { nonSuperAdminCanMatch, superAdminCanMatch } from '../core/guards/role-match.guard';

/** Shown in shell header; keep labels aligned with sidebar nav. */
export const shellTitleKey = 'shellTitle' as const;

export const siteRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: AppPaths.dashboard,
  },
  {
    path: AppPaths.dashboard,
    data: { [shellTitleKey]: 'nav.dashboard' },
    loadComponent: () => import('../features/dashboard/dashboard').then((m) => m.Dashboard),
  },
  {
    path: AppPaths.analytics,
    data: { [shellTitleKey]: 'nav.analytics' },
    loadComponent: () => import('../features/analytics/components/analytics').then((m) => m.AnalyticsPage),
  },
  {
    path: AppPaths.pendingRequests,
    data: { [shellTitleKey]: 'sidebar.links.pendingRequests' },
    loadComponent: () =>
      import('../features/pending-requests/pending-requests').then((m) => m.PendingRequestsPage),
  },
  {
    path: AppPaths.organizations,
    pathMatch: 'full',
    data: { [shellTitleKey]: 'sidebar.links.organizations' },
    loadComponent: () =>
      import('../features/organizations/organizations').then((m) => m.OrganizationsPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.orders}`,
    pathMatch: 'full',
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.orders' },
    loadComponent: () => import('../features/orders/orders').then((m) => m.OrdersPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.orders}/:orderId`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'ordersPage.detail.pageTitle' },
    loadComponent: () =>
      import('../features/orders/pages/order-detail.page').then((m) => m.OrderDetailPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.returnsRefunds}`,
    pathMatch: 'full',
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.returnsRefunds' },
    loadComponent: () =>
      import('../features/returns/pages/returns.page').then((m) => m.ReturnsPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.returnsRefunds}/:id`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'returnsPage.detail.title' },
    loadComponent: () =>
      import('../features/returns/pages/return-detail.page').then((m) => m.ReturnDetailPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.warehouses}`,
    pathMatch: 'full',
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.warehouses' },
    loadComponent: () =>
      import('../features/warehouses/pages/warehouses.page').then((m) => m.WarehousesPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.warehouses}/:warehouseId`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'warehousesPage.detail.title' },
    loadComponent: () =>
      import('../features/warehouses/pages/warehouse-detail.page').then(
        (m) => m.WarehouseDetailPage,
      ),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}`,
    pathMatch: 'full',
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.suppliers' },
    loadComponent: () =>
      import('../features/suppliers/pages/suppliers.page').then((m) => m.SuppliersPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}/:supplierId/purchase-orders/new`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'purchaseOrdersPage.addModal.titleAdd' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-form.page').then(
        (m) => m.PurchaseOrderFormPage,
      ),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId/edit`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'purchaseOrdersPage.addModal.titleEdit' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-form.page').then(
        (m) => m.PurchaseOrderFormPage,
      ),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId/analysis`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'purchaseOrdersPage.analysis.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-analysis.page').then(
        (m) => m.PurchaseOrderAnalysisPage,
      ),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'purchaseOrdersPage.detail.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-detail.page').then(
        (m) => m.PurchaseOrderDetailPage,
      ),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.suppliers}/:id`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'suppliersPage.detail.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/supplier-detail.page').then((m) => m.SupplierDetailPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId/${AppPaths.inventory}`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.inventory' },
    loadComponent: () => import('../features/inventory/inventory').then((m) => m.InventoryPage),
  },
  {
    path: `${AppPaths.organizations}/:tenantId`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'organizationsPage.detail.title' },
    loadComponent: () =>
      import('../features/organizations/pages/organization-detail.page').then(
        (m) => m.OrganizationDetailPage,
      ),
  },
  {
    path: AppPaths.categories,
    data: { [shellTitleKey]: 'sidebar.links.categories' },
    loadComponent: () => import('../features/categories/categories').then((m) => m.CategoriesPage),
  },
  {
    path: AppPaths.couponCodes,
    data: { [shellTitleKey]: 'sidebar.links.couponCodes' },
    loadComponent: () =>
      import('../features/coupons/pages/coupons.page').then((m) => m.CouponsPage),
  },
  {
    path: AppPaths.suppliers,
    pathMatch: 'full',
    data: { [shellTitleKey]: 'sidebar.links.suppliers' },
    loadComponent: () =>
      import('../features/suppliers/pages/suppliers.page').then((m) => m.SuppliersPage),
  },
  {
    path: `${AppPaths.suppliers}/:supplierId/purchase-orders/new`,
    data: { [shellTitleKey]: 'purchaseOrdersPage.addModal.titleAdd' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-form.page').then(
        (m) => m.PurchaseOrderFormPage,
      ),
  },
  {
    path: `${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId/edit`,
    data: { [shellTitleKey]: 'purchaseOrdersPage.addModal.titleEdit' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-form.page').then(
        (m) => m.PurchaseOrderFormPage,
      ),
  },
  {
    path: `${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId/analysis`,
    data: { [shellTitleKey]: 'purchaseOrdersPage.analysis.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-analysis.page').then(
        (m) => m.PurchaseOrderAnalysisPage,
      ),
  },
  {
    path: `${AppPaths.suppliers}/:supplierId/purchase-orders/:purchaseOrderId`,
    data: { [shellTitleKey]: 'purchaseOrdersPage.detail.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/purchase-order-detail.page').then(
        (m) => m.PurchaseOrderDetailPage,
      ),
  },
  {
    path: `${AppPaths.suppliers}/:id`,
    data: { [shellTitleKey]: 'suppliersPage.detail.title' },
    loadComponent: () =>
      import('../features/suppliers/pages/supplier-detail.page').then((m) => m.SupplierDetailPage),
  },
  {
    path: AppPaths.warehouses,
    data: { [shellTitleKey]: 'sidebar.links.warehouses' },
    loadComponent: () =>
      import('../features/warehouses/pages/warehouses.page').then((m) => m.WarehousesPage),
  },
  {
    path: `${AppPaths.warehouses}/:warehouseId/products/new`,
    data: { [shellTitleKey]: 'inventoryPage.retail.actions.add' },
    loadComponent: () =>
      import('../features/inventory/pages/inventory-item-form.page').then(
        (m) => m.InventoryItemFormPage,
      ),
  },
  {
    path: `${AppPaths.warehouses}/:warehouseId`,
    data: { [shellTitleKey]: 'warehousesPage.detail.title' },
    loadComponent: () =>
      import('../features/warehouses/pages/warehouse-detail.page').then(
        (m) => m.WarehouseDetailPage,
      ),
  },
  {
    path: `${AppPaths.inventory}/new`,
    data: { [shellTitleKey]: 'inventoryPage.retail.actions.add' },
    loadComponent: () =>
      import('../features/inventory/pages/inventory-item-form.page').then(
        (m) => m.InventoryItemFormPage,
      ),
  },
  {
    path: `${AppPaths.inventory}/:itemId/edit`,
    data: { [shellTitleKey]: 'inventoryPage.addModal.titleEdit' },
    loadComponent: () =>
      import('../features/inventory/pages/inventory-item-form.page').then(
        (m) => m.InventoryItemFormPage,
      ),
  },
  {
    path: AppPaths.inventory,
    pathMatch: 'full',
    data: { [shellTitleKey]: 'sidebar.links.inventory' },
    loadComponent: () => import('../features/inventory/inventory').then((m) => m.InventoryPage),
  },
  {
    path: AppPaths.orders,
    pathMatch: 'full',
    data: { [shellTitleKey]: 'sidebar.links.orders' },
    loadComponent: () => import('../features/orders/orders').then((m) => m.OrdersPage),
  },
  {
    path: `${AppPaths.orders}/:orderId`,
    data: { [shellTitleKey]: 'ordersPage.detail.pageTitle' },
    loadComponent: () =>
      import('../features/orders/pages/order-detail.page').then((m) => m.OrderDetailPage),
  },
  {
    path: AppPaths.returnsRefunds,
    pathMatch: 'full',
    canMatch: [nonSuperAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.returnsRefunds' },
    loadComponent: () =>
      import('../features/returns/pages/returns.page').then((m) => m.ReturnsPage),
  },
  {
    path: `${AppPaths.returnsRefunds}/:id`,
    canMatch: [nonSuperAdminCanMatch],
    data: { [shellTitleKey]: 'returnsPage.detail.title' },
    loadComponent: () =>
      import('../features/returns/pages/return-detail.page').then((m) => m.ReturnDetailPage),
  },
  {
    path: AppPaths.customers,
    data: { [shellTitleKey]: 'sidebar.links.customers' },
    loadComponent: () => import('../features/customers/customers').then((m) => m.CustomersPage),
  },
  {
    path: `${AppPaths.customers}/:customerId`,
    data: { [shellTitleKey]: 'customersPage.detail.pageTitle' },
    loadComponent: () =>
      import('../features/customers/pages/customer-detail.page').then((m) => m.CustomerDetailPage),
  },
  {
    path: AppPaths.allAdmins,
    data: { [shellTitleKey]: 'sidebar.links.allAdmins' },
    loadComponent: () => import('../features/all-admins/components/all-admins').then((m) => m.AllAdminsPage),
  },
  {
    path: AppPaths.staffMembers,
    data: { [shellTitleKey]: 'sidebar.links.staffMembers' },
    loadComponent: () =>
      import('../features/staff-members/components/staff-members').then((m) => m.StaffMembersPage),
  },
  {
    path: `${AppPaths.staffMembers}/:staffId`,
    data: { [shellTitleKey]: 'staffMembers.detail.pageTitle' },
    loadComponent: () =>
      import('../features/staff-members/pages/staff-member-detail.page').then(
        (m) => m.StaffMemberDetailPage,
      ),
  },
  {
    path: AppPaths.settings,
    data: { [shellTitleKey]: 'sidebar.links.settings' },
    loadComponent: () => import('../features/settings/settings').then((m) => m.SettingsPage),
  },
  {
    path: AppPaths.profile,
    data: { [shellTitleKey]: 'sidebar.links.profile' },
    loadComponent: () => import('../features/profile/profile').then((m) => m.ProfilePage),
  },
  {
    path: AppPaths.auditLogs,
    data: { [shellTitleKey]: 'sidebar.links.auditLogs' },
    loadComponent: () => import('../features/audit-logs/components/audit-logs').then((m) => m.AuditLogsPage),
  },
  {
    path: AppPaths.reports,
    data: { [shellTitleKey]: 'sidebar.links.reports' },
    loadComponent: () => import('../features/reports/reports').then((m) => m.ReportsPage),
  },
  {
    path: AppPaths.menuCatalog,
    data: { [shellTitleKey]: 'sidebar.links.menuCatalog' },
    loadComponent: () => import('../features/menu-catalog/menu-catalog').then((m) => m.MenuCatalogPage),
  },
  {
    path: AppPaths.recipe,
    data: { [shellTitleKey]: 'sidebar.links.recipe' },
    loadComponent: () => import('../features/recipe/recipe').then((m) => m.RecipePage),
  },
  {
    path: AppPaths.ingredients,
    data: { [shellTitleKey]: 'sidebar.links.ingredients' },
    loadComponent: () =>
      import('../features/ingredients/ingredients').then((m) => m.IngredientsPage),
  },
  {
    path: AppPaths.tables,
    data: { [shellTitleKey]: 'sidebar.links.tables' },
    loadComponent: () =>
      import('../features/tables/pages/tables.page').then((m) => m.TablesPage),
  },
  {
    path: AppPaths.reservations,
    data: { [shellTitleKey]: 'sidebar.links.reservations' },
    loadComponent: () =>
      import('../features/reservations/pages/reservations.page').then((m) => m.ReservationsPage),
  },
  {
    path: AppPaths.moduleNames,
    data: { [shellTitleKey]: 'moduleNamesPage.title' },
    loadComponent: () =>
      import('../features/module-names/pages/module-names.page').then((m) => m.ModuleNamesPage),
  },
  {
    path: AppPaths.support,
    data: { [shellTitleKey]: 'sidebar.links.support' },
    loadChildren: () => import('../features/support/support.routes').then((m) => m.supportRoutes),
  },
  {
    path: AppPaths.cashSettlements,
    pathMatch: 'full',
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.settlements' },
    loadComponent: () =>
      import('../features/cash-settlements/pages/cash-settlement-tenants.page').then(
        (m) => m.CashSettlementTenantsPage,
      ),
  },
  {
    path: AppPaths.cashSettlements,
    pathMatch: 'full',
    canMatch: [nonSuperAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.settlements' },
    loadComponent: () =>
      import('../features/cash-settlements/pages/cash-settlements.page').then(
        (m) => m.CashSettlementsPage,
      ),
  },
  {
    path: `${AppPaths.cashSettlements}/tenants/:tenantId`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'sidebar.links.settlements' },
    loadComponent: () =>
      import('../features/cash-settlements/pages/cash-settlements.page').then(
        (m) => m.CashSettlementsPage,
      ),
  },
  {
    path: `${AppPaths.cashSettlements}/tenants/:tenantId/:id`,
    canMatch: [superAdminCanMatch],
    data: { [shellTitleKey]: 'cashSettlementsPage.detail.title' },
    loadComponent: () =>
      import('../features/cash-settlements/pages/cash-settlement-detail.page').then(
        (m) => m.CashSettlementDetailPage,
      ),
  },
  {
    path: `${AppPaths.cashSettlements}/:id`,
    canMatch: [nonSuperAdminCanMatch],
    data: { [shellTitleKey]: 'cashSettlementsPage.detail.title' },
    loadComponent: () =>
      import('../features/cash-settlements/pages/cash-settlement-detail.page').then(
        (m) => m.CashSettlementDetailPage,
      ),
  },
];
